import React, { useState, forwardRef, createRef, useRef } from 'react';
import _merge from 'lodash/merge';

import { useCombinedRefs, getContext } from 'common';
import useHover from 'common/useHover';
import RadioButtonRipple from './RadioButtonRipple';
import RadioButtonIcon from './RadioButtonIcon';

import RadioButtonLabelContent from './RadioButtonLabelContent';

import Styled from './styles';

const INPUT_CONFIG = {
  type: 'radio'
};

export interface RadioButtonProps extends Omit<React.ComponentPropsWithRef<'span'>, 'onChange'> {
  /** Callback fired when the state is changed. Signature: `function(event: object) => void`, event: The event source of the callback. The new checked state: boolean `event.target.checked`. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** If `true`, the component is checked. Note: The native input element's `checked` attribute will be set using this prop. */
  checked?: boolean;
  /** If `true`, the component will be disabled. Note: The native input element's `disabled` attribute will be set using this prop. */
  disabled?: boolean;
  /** If `true`, the component will automatically get focus when the page loads. Note: The native input element's `autofocus` attribute will be set using this prop. */
  autoFocus?: boolean;
  /**  Attributes applied to the input element. (excluding { type: 'radio' }). Note: DOM attributes should be camelCased. */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /** To override the icon, in addition to all RadioButton props the icon component will receive `focused` and `hovered` */
  renderIcon?: (any) => React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * Uncontrolled RadioButton.
 * Any other props than the `RadioButton` props will be provided to the root element.
 * To alter the DOM attributes applied to the internal `input` element, please use `inputProps`.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * */
const RadioButton = forwardRef<HTMLElement, RadioButtonProps>((props: RadioButtonProps, ref) => getContext(({ theme }) => {
  const {
    autoFocus,
    checked,
    disabled,
    onChange,
    inputProps,
    renderIcon,
    theme: themeProp,
    ...wrapperProps
  } = props;

  const resolvedTheme = _merge({}, theme.RADIO_BUTTON, themeProp);

  const rippleRef = useRef(null);
  const inputEl = createRef<HTMLInputElement>();
  const combinedRef = useCombinedRefs(ref, inputEl);
  const [hoverRef, hovered] = useHover();
  const [focused, setIsFocused] = useState(autoFocus);
  const resolvedWrapperProps = {
    ...wrapperProps,
    $disabled: disabled,
  };

  const mapComponentPropsToNativeAttributes = () => ({
    autoFocus,
    checked,
    disabled,
    onChange
  });

  const runRipple = () => {
    rippleRef.current.runRipple();
  };

  const asyncHideRipple = () => {
    rippleRef.current.asyncHideRipple();
  };

  const resolvedInputProps = {
    ...mapComponentPropsToNativeAttributes(),
    ...inputProps,
    ref: combinedRef,
    onMouseDown: (data) => { !checked && runRipple(); inputProps.onMouseDown && inputProps.onMouseDown(data); },
    onMouseUp: (data) => { !checked && asyncHideRipple(); inputProps.onMouseUp && inputProps.onMouseUp(data); },
    onMouseOut: (data) => { !checked && asyncHideRipple(); inputProps.onMouseOut && inputProps.onMouseOut(data); },
    onClick: (data) => { inputEl && inputEl.current && inputEl.current.focus(); inputProps.onClick && inputProps.onClick(data); },
    onFocus: (data) => { setIsFocused(true); inputProps.onFocus && inputProps.onFocus(data); },
    onBlur: (data) => { setIsFocused(false); inputProps.onBlur && inputProps.onBlur(data); },
    ...INPUT_CONFIG,
    $theme: resolvedTheme
  };

  return (
    <Styled.Wrapper {...resolvedWrapperProps} ref={hoverRef}>
      {renderIcon({ ...props, focused, hovered, $theme: resolvedTheme })}
      {focused && <Styled.FocusBorder $theme={resolvedTheme} />}
      {hovered && <Styled.HoveredStyle checked={checked} $theme={resolvedTheme} />}
      <RadioButtonRipple ref={rippleRef} $theme={resolvedTheme} />
      <Styled.Input {...resolvedInputProps} />
    </Styled.Wrapper>
  );
}));

RadioButton.defaultProps = {
  theme: {},
  onChange: undefined,
  checked: false,
  disabled: false,
  autoFocus: false,
  inputProps: {},
  renderIcon: props => <RadioButtonIcon {...props} /> // eslint-disable-line 
};

RadioButton.displayName = 'RadioButton';

export {
  RadioButton as default,
  RadioButtonLabelContent,
};
